/* eslint-disable @typescript-eslint/no-explicit-any */

import { api, ApiResponse } from '@/api/api';
import { JOB_URLS } from '@/api/url';
import { ApplicationQuestion } from '@/types/ApplicationQuestion';
import { Candidate, CandidateWithMatchInfo } from '@/types/Candidate';
import { Job, JobBasicInfo, JobRequirements } from '@/types/Job';
import { JobDescription } from '@/types/JobDescription';
import { EmploymentType, JobStatus, WorkType } from '@/types/enums';
import { isAxiosError } from 'axios';

export interface ExtractJDPayload {
  file: File;
}

export const apiExtractJD = async ({
  payload
}: {
  payload: ExtractJDPayload;
}): Promise<ApiResponse<JobDescription>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await api.post<ApiResponse<JobDescription>>(JOB_URLS.EXTRACT_JD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AnalyzeJobRequirementsPayload {
  job_title: string;
  job_description: string;
}

export interface AnalyzeJobRequirementsResponse {
  requirements: string[];
  nice_to_haves: string[];
  position_tags: string[];
}

export const apiAnalyzeJobRequirements = async ({
  payload
}: {
  payload: AnalyzeJobRequirementsPayload;
}): Promise<ApiResponse<AnalyzeJobRequirementsResponse>> => {
  try {
    const response = await api.post<ApiResponse<AnalyzeJobRequirementsResponse>>(
      JOB_URLS.ANALYZE_REQUIREMENTS,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobSummaryResponse {
  active_jobs: number;
  jobs_awaiting_review: number;
  candidates_in_pipeline: number;
}

export const apiGetJobSummary = async (): Promise<ApiResponse<GetJobSummaryResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetJobSummaryResponse>>(JOB_URLS.GET_SUMMARY);

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobsResponse {
  jobs: JobBasicInfo[];
  current_page: number;
  total_page: number;
}

export interface GetJobsParams {
  client_id: string;
  page: number;
  limit: number;
  keyword?: string;
  archived?: boolean;
}

export const apiGetJobs = async ({
  params
}: {
  params: GetJobsParams;
}): Promise<ApiResponse<GetJobsResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetJobsResponse>>(JOB_URLS.GET_ALL, {
      params
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface CreateJobPayload {
  client_id: string;
  job_title: string;
  category: string;
  currency: string;
  salary_from: number | null;
  salary_to: number | null;
  employment_type: EmploymentType;
  work_type: WorkType;
  location: string;
  job_description: string;
  contact_name: string;
  contact_role: string;
  contact_email: string;
  contact_phone: string;
  requirements: {
    requirements: string[];
    nice_to_haves: string[];
  };
  position_tags: string[];
}

export const apiCreateJob = async ({
  payload
}: {
  payload: CreateJobPayload;
}): Promise<ApiResponse<Job>> => {
  try {
    const response = await api.post<ApiResponse<Job>>(JOB_URLS.CREATE, payload);

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UploadJDPayload {
  file: File;
}

export const apiUploadJD = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: UploadJDPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await api.post<ApiResponse<void>>(JOB_URLS.UPLOAD_JD(jobId), formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobDetailsResponse {
  job_info: Job;
  total_candidates: number;
  waiting_review: number;
  candidate_pass_screening: number;
  match_candidates: CandidateWithMatchInfo[];
}

export const apiGetJobDetails = async ({
  jobId
}: {
  jobId: string;
}): Promise<ApiResponse<GetJobDetailsResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetJobDetailsResponse>>(JOB_URLS.GET_DETAILS(jobId));

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobPayload {
  job_title: string;
  category: string;
  currency: string;
  salary_from: number | null;
  salary_to: number | null;
  employment_type: EmploymentType;
  work_type: WorkType;
  location: string;
  job_description: string;
  contact_name: string;
  contact_role: string;
  contact_email: string;
  contact_phone: string;
  requirements: JobRequirements;
  position_tags: string[];
}

export const apiUpdateJob = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: UpdateJobPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(JOB_URLS.UPDATE(jobId), payload);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobStatusPayload {
  status: JobStatus;
}

export const apiUpdateJobStatus = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: UpdateJobStatusPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(JOB_URLS.UPDATE_STATUS(jobId), payload);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetJobMatchCandidates = async ({
  jobId
}: {
  jobId: string;
}): Promise<ApiResponse<CandidateWithMatchInfo[]>> => {
  try {
    const response = await api.get<ApiResponse<CandidateWithMatchInfo[]>>(
      JOB_URLS.GET_MATCH_CANDIDATES(jobId)
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AddCandidateToJobPayload {
  candidate_id: string;
}

export const apiAddCandidateToJob = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: AddCandidateToJobPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(JOB_URLS.ADD_CANDIDATE(jobId), payload);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobAvailableCandidatesParams {
  page: number;
  limit: number;
  keyword: string;
}

export interface GetJobAvailableCandidatesResponse {
  candidates: Candidate[];
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
}

export const apiGetJobAvailableCandidates = async ({
  jobId,
  params
}: {
  jobId: string;
  params: GetJobAvailableCandidatesParams;
}): Promise<ApiResponse<GetJobAvailableCandidatesResponse>> => {
  try {
    // Custom params serializer to handle arrays without [] notation
    const paramsSerializer = (params: any) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (no [] notation)
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    };

    const response = await api.get<ApiResponse<GetJobAvailableCandidatesResponse>>(
      JOB_URLS.GET_AVAILABLE_CANDIDATES(jobId),
      {
        params,
        paramsSerializer
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobPublishJobBoardPayload {
  published: boolean;
  category?: string;
}

export const apiUpdateJobPublishJobBoard = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: UpdateJobPublishJobBoardPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(
      JOB_URLS.UPDATE_PUBLISH_STATUS(jobId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetPublishedJobsParams {
  page: number;
  limit: number;
  keyword?: string;
  archived?: boolean;
}

export const apiGetPublishedJobs = async ({
  params
}: {
  params: GetPublishedJobsParams;
}): Promise<ApiResponse<GetJobsResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetJobsResponse>>(JOB_URLS.GET_PUBLISH_JOB_BOARD, {
      params
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobCategoriesResponse {
  categories: string[];
}

export const apiGetJobCategories = async (): Promise<ApiResponse<GetJobCategoriesResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetJobCategoriesResponse>>(JOB_URLS.GET_CATEGORIES);

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobApplicationQuestionsPayload {
  application_questions: ApplicationQuestion[];
}

export const apiUpdateJobApplicationQuestions = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: UpdateJobApplicationQuestionsPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(
      JOB_URLS.UPDATE_APPLICATION_QUESTIONS(jobId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};
